import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import DateInputBR from "@/components/DateInputBR";
import { fmtDate } from "@/lib/format";
import {
  Activity, Search, Filter, RefreshCw, RotateCcw, Calendar,
  Clock, AlertTriangle, CheckCircle2, Users2, Pencil,
} from "lucide-react";
import { toast } from "sonner";

const BUCKETS = [
  { key: "ate_15",    label: "até 15 dias" },
  { key: "16_30",     label: "16 a 30 dias" },
  { key: "31_60",     label: "31 a 60 dias" },
  { key: "61_90",     label: "61 a 90 dias" },
  { key: "91_180",    label: "91 a 180 dias" },
  { key: "acima_180", label: "acima de 180 dias" },
];

function StatCard({ label, value, tone = "green", onClick, testId }) {
  const tones = {
    green:  "border-[#234935]/25 bg-[#234935]/5 text-[#234935]",
    amber:  "border-amber-300/50 bg-amber-50 text-amber-800",
    blue:   "border-blue-300/50 bg-blue-50 text-blue-800",
    rose:   "border-rose-300/50 bg-rose-50 text-rose-800",
    slate:  "border-slate-300/50 bg-slate-50 text-slate-700",
  };
  return (
    <button type="button" onClick={onClick}
            className={`rounded-xl border p-4 text-left transition hover:shadow-sm ${tones[tone]}`}
            data-testid={testId}>
      <div className="text-[10.5px] uppercase tracking-[0.15em] font-medium opacity-80">{label}</div>
      <div className="mt-1 font-display text-3xl font-semibold">{value}</div>
    </button>
  );
}

function EditLeaveDialog({ leave, types, isMaster, onClose, onDone }) {
  const [tipo, setTipo] = useState(leave?.tipo || "");
  const [dataInicio, setDataInicio] = useState(leave?.data_inicio || "");
  const [dataPrev, setDataPrev] = useState(leave?.data_prev_retorno || "");
  const [dataRetorno, setDataRetorno] = useState(leave?.data_retorno_efetivo || "");
  const [obs, setObs] = useState(leave?.observacoes || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!leave) return;
    setTipo(leave.tipo || "");
    setDataInicio(leave.data_inicio || "");
    setDataPrev(leave.data_prev_retorno || "");
    setDataRetorno(leave.data_retorno_efetivo || "");
    setObs(leave.observacoes || "");
  }, [leave]);

  if (!leave) return null;
  const encerrado = leave.situacao === "encerrado";

  const submit = async () => {
    if (!tipo) { toast.error("Tipo é obrigatório."); return; }
    if (!dataInicio) { toast.error("Data de início é obrigatória."); return; }
    // Só envia campos que realmente mudaram (evita 403 desnecessário para não-master)
    const payload = {};
    if (tipo !== leave.tipo) payload.tipo = tipo;
    if (dataInicio !== (leave.data_inicio || "")) payload.data_inicio = dataInicio;
    if ((dataPrev || "") !== (leave.data_prev_retorno || "")) {
      payload.data_prev_retorno = dataPrev || null;
    }
    if (encerrado && (dataRetorno || "") !== (leave.data_retorno_efetivo || "")) {
      payload.data_retorno_efetivo = dataRetorno || null;
    }
    if ((obs || "") !== (leave.observacoes || "")) {
      payload.observacoes = obs || null;
    }
    if (Object.keys(payload).length === 0) {
      toast.info("Nenhuma alteração para salvar.");
      onClose();
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/employee-leaves/${leave.id}`, payload);
      toast.success("Afastamento atualizado.");
      onClose();
      onDone && onDone();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!leave} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[560px]" data-testid="edit-leave-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-[#234935]" /> Editar afastamento
          </DialogTitle>
          <DialogDescription>
            <b>{leave.nome}</b> · matrícula {leave.matricula} · {encerrado ? "Encerrado" : "Em andamento"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Tipo *</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger data-testid="edit-leave-tipo"><SelectValue placeholder="Selecione o tipo" /></SelectTrigger>
              <SelectContent>
                {types.map((t) => (
                  <SelectItem key={t.codigo} value={t.codigo}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Data de início *</Label>
              <DateInputBR value={dataInicio}
                     onChange={setDataInicio}
                     disabled={!isMaster && dataInicio === (leave.data_inicio || "")}
                     testId="edit-leave-inicio" />
              {!isMaster && (
                <div className="text-[10.5px] text-slate-500 mt-1">Somente Master pode alterar.</div>
              )}
            </div>
            {!encerrado ? (
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Previsão de retorno</Label>
                <DateInputBR value={dataPrev}
                       onChange={setDataPrev}
                       testId="edit-leave-prev" />
              </div>
            ) : (
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Data de retorno efetivo</Label>
                <DateInputBR value={dataRetorno}
                       onChange={setDataRetorno}
                       disabled={!isMaster}
                       testId="edit-leave-retorno" />
                {!isMaster && (
                  <div className="text-[10.5px] text-slate-500 mt-1">Somente Master pode alterar.</div>
                )}
              </div>
            )}
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Observações</Label>
            <Textarea rows={3} value={obs} onChange={(e) => setObs(e.target.value)}
                      placeholder="Correção / justificativa da alteração (opcional)"
                      data-testid="edit-leave-obs" />
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] text-amber-900">
            As alterações são registradas em auditoria (antes/depois, usuário e horário) e não duplicam o registro atual.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-leave-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={busy}
                  className="bg-[#234935] hover:bg-[#2c5c43] text-white"
                  data-testid="edit-leave-save">
            {busy ? "Salvando…" : "Salvar alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReturnDialog({ leave, onClose, onDone }) {
  const [dt, setDt] = useState(new Date().toISOString().slice(0, 10));
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);
  if (!leave) return null;
  const submit = async () => {
    if (!dt) { toast.error("Informe a data de retorno."); return; }
    setBusy(true);
    try {
      await api.post(`/employee-leaves/${leave.id}/return`, {
        data_retorno_efetivo: dt,
        observacoes_retorno: obs || null,
      });
      toast.success("Retorno registrado. Colaborador reativado.");
      onClose();
      onDone && onDone();
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!leave} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[520px]" data-testid="return-leave-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4 text-emerald-600" /> Registrar retorno de afastamento
          </DialogTitle>
          <DialogDescription>
            <b>{leave.nome}</b> · matrícula {leave.matricula} · {leave.tipo_label} · início {fmtDate(leave.data_inicio)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Data efetiva do retorno *</Label>
            <DateInputBR value={dt} onChange={setDt} testId="return-date" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Observação (opcional)</Label>
            <Textarea rows={3} value={obs} onChange={(e) => setObs(e.target.value)}
                      placeholder="Ex: Alta médica com liberação para retomar as atividades."
                      data-testid="return-obs" />
          </div>
          <div className="rounded-md border border-blue-200 bg-blue-50 p-2.5 text-[11.5px] text-blue-900">
            Ao registrar o retorno, o afastamento será encerrado e o colaborador voltará automaticamente à situação <b>Ativo</b>, mantendo sua lotação (setor, sub-setor e centro de custo).
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="return-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={busy}
                  className="bg-[#234935] hover:bg-[#2c5c43] text-white"
                  data-testid="return-confirm">
            {busy ? "Registrando…" : "Confirmar retorno"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EmployeeLeavesPage() {
  const { user } = useAuth();
  const { current: currentCompany } = useCompany();

  const canWrite = ["master", "rh_admin", "rh_operacional"].includes(user?.role);

  const [types, setTypes] = useState([]);
  const [summary, setSummary] = useState({ total: 0, by_type: [], by_bucket: {} });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    situacao: "em_andamento",
    tipo: "all",
    bucket: "all",
    search: "",
  });
  const [returnTarget, setReturnTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const isMaster = user?.role === "master";

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, sRes, lRes] = await Promise.all([
        api.get("/employee-leaves/types"),
        api.get("/employee-leaves/summary", {
          params: {
            company_id: currentCompany?.id,
            situacao: filters.situacao !== "todos" ? filters.situacao : undefined,
          },
        }),
        api.get("/employee-leaves", {
          params: {
            company_id: currentCompany?.id,
            situacao: filters.situacao !== "todos" ? filters.situacao : undefined,
            tipo: filters.tipo !== "all" ? filters.tipo : undefined,
            bucket: filters.bucket !== "all" ? filters.bucket : undefined,
            page: 1, page_size: 500,
          },
        }),
      ]);
      setTypes(tRes.data?.types || []);
      setSummary(sRes.data || { total: 0, by_type: [], by_bucket: {} });
      setItems(lRes.data?.items || []);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [
    currentCompany?.id, filters.situacao, filters.tipo, filters.bucket,
  ]);

  const filtered = useMemo(() => {
    const q = (filters.search || "").trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      (i.nome || "").toLowerCase().includes(q) ||
      (i.matricula || "").toLowerCase().includes(q) ||
      (i.cargo || "").toLowerCase().includes(q)
    );
  }, [items, filters.search]);

  return (
    <div className="space-y-6" data-testid="employee-leaves-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-[#C5A059]/25 bg-gradient-to-br from-[#234935] via-[#1C3A2B] to-[#0F1C14] p-8 text-white">
        <div aria-hidden className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#C5A059]/20 blur-[80px]" />
        <div className="relative">
          <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#C5A059] font-medium flex items-center gap-2">
            <Activity className="h-3 w-3" /> Gestão de Pessoas
          </div>
          <h1 className="mt-2 font-display text-3xl md:text-[34px] font-semibold tracking-tight">
            Afastamentos
          </h1>
          <p className="mt-2 text-[13.5px] text-white/70 max-w-[720px]">
            Histórico oficial dos afastamentos de médio e longo prazo por colaborador —
            Auxílio-Doença, Acidente de Trabalho, Aposentadorias, Cárcere, Maternidade e outros.
            O retorno efetivo (não a previsão) encerra o afastamento e reativa o colaborador automaticamente.
          </p>
        </div>
      </div>

      {/* KPIs por faixa de tempo */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
        <StatCard label="Total em andamento" value={summary.total || 0} tone="green"
                  testId="stat-total"
                  onClick={() => setFilters({ ...filters, bucket: "all", tipo: "all" })} />
        {BUCKETS.map((b) => (
          <StatCard key={b.key} label={b.label} value={summary.by_bucket?.[b.key] || 0}
                    tone={b.key === "acima_180" ? "rose" : (b.key.startsWith("91") ? "amber" : "slate")}
                    testId={`stat-bucket-${b.key}`}
                    onClick={() => setFilters({ ...filters, bucket: b.key, tipo: "all" })} />
        ))}
      </div>

      {/* Distribuição por tipo */}
      {summary.by_type?.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-[0.15em] text-slate-500 font-medium">
            <Users2 className="h-3.5 w-3.5" /> Distribuição por tipo
          </div>
          <div className="flex flex-wrap gap-2">
            {summary.by_type.map((t) => (
              <button key={t.codigo}
                      onClick={() => setFilters({ ...filters, tipo: t.codigo, bucket: "all" })}
                      className={`rounded-full border px-3 py-1.5 text-[12px] transition hover:shadow-sm ${
                        filters.tipo === t.codigo
                          ? "border-[#234935] bg-[#234935] text-white"
                          : "border-slate-300 bg-white text-slate-700 hover:border-[#234935]"
                      }`}
                      data-testid={`chip-type-${t.codigo}`}>
                {t.label} <b className="ml-1">· {t.total}</b>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end rounded-xl border border-slate-200 bg-white p-4">
        <div>
          <Label className="text-[11px] uppercase tracking-wider">Situação</Label>
          <Select value={filters.situacao} onValueChange={(v) => setFilters({ ...filters, situacao: v })}>
            <SelectTrigger data-testid="filter-situacao"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="em_andamento">Em andamento</SelectItem>
              <SelectItem value="encerrado">Encerrado</SelectItem>
              <SelectItem value="todos">Todos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider">Tipo</Label>
          <Select value={filters.tipo} onValueChange={(v) => setFilters({ ...filters, tipo: v })}>
            <SelectTrigger data-testid="filter-tipo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              {types.map((t) => (
                <SelectItem key={t.codigo} value={t.codigo}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider">Faixa de tempo</Label>
          <Select value={filters.bucket} onValueChange={(v) => setFilters({ ...filters, bucket: v })}>
            <SelectTrigger data-testid="filter-bucket"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as faixas</SelectItem>
              {BUCKETS.map((b) => (<SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider">Buscar</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Nome, matrícula, cargo"
                   value={filters.search}
                   onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                   className="pl-8" data-testid="filter-search" />
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-2">
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-slate-500">
            Nenhum afastamento encontrado com os filtros atuais.
          </div>
        ) : (
          <Table data-testid="leaves-table">
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Colaborador</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Início</TableHead>
                <TableHead>Previsão</TableHead>
                <TableHead>Retorno</TableHead>
                <TableHead>Dias</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((l) => (
                <TableRow key={l.id} data-testid={`leave-row-${l.id}`}>
                  <TableCell>
                    <div className="font-medium text-[13px]">{l.nome}</div>
                    <div className="text-[11.5px] text-slate-500">Matrícula {l.matricula || "—"}</div>
                  </TableCell>
                  <TableCell className="text-[12.5px]">{l.cargo || "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[11px]">{l.tipo_label}</Badge>
                  </TableCell>
                  <TableCell className="text-[12.5px]">{l.data_inicio ? fmtDate(l.data_inicio) : "—"}</TableCell>
                  <TableCell className="text-[12.5px]">{l.data_prev_retorno ? fmtDate(l.data_prev_retorno) : "—"}</TableCell>
                  <TableCell className="text-[12.5px]">{l.data_retorno_efetivo ? fmtDate(l.data_retorno_efetivo) : "—"}</TableCell>
                  <TableCell className="text-[12.5px] font-semibold">{l.dias_afastado}</TableCell>
                  <TableCell>
                    {l.situacao === "em_andamento" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] text-amber-800">
                        <Clock className="h-3 w-3" /> Em andamento
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px] text-emerald-800">
                        <CheckCircle2 className="h-3 w-3" /> Encerrado
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1.5">
                      {canWrite && (
                        <Button size="sm" variant="outline"
                                onClick={() => setEditTarget(l)}
                                data-testid={`leave-edit-${l.id}`}>
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                        </Button>
                      )}
                      {canWrite && l.situacao === "em_andamento" && (
                        <Button size="sm" variant="outline"
                                onClick={() => setReturnTarget(l)}
                                className="text-emerald-700 hover:bg-emerald-50"
                                data-testid={`leave-return-${l.id}`}>
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Registrar retorno
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <ReturnDialog leave={returnTarget} onClose={() => setReturnTarget(null)} onDone={load} />
      <EditLeaveDialog leave={editTarget} types={types} isMaster={isMaster}
                       onClose={() => setEditTarget(null)} onDone={load} />
    </div>
  );
}
